import { useState, type FormEvent } from "react";
import Button from "@/components/Button/Button";
import styles from "./ContactForm.module.css";

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

const inquiryOptions = [
  "Individual therapy",
  "Couples therapy",
  "Ketamine-Assisted Psychotherapy (KAP)",
  "Clinical supervision",
  "General question",
] as const;

export default function ContactForm() {
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state.kind === "submitting") return;

    const form = e.currentTarget;
    const data = new FormData(form);

    if (data.get("website")) {
      setState({ kind: "success" });
      form.reset();
      return;
    }

    const payload = {
      name: String(data.get("name") || "").trim(),
      email: String(data.get("email") || "").trim(),
      phone: String(data.get("phone") || "").trim(),
      inquiry: String(data.get("inquiry") || "").trim(),
      message: String(data.get("message") || "").trim(),
      consentTelehealth: data.get("consentTelehealth") === "on",
    };

    if (!payload.name || !payload.email || !payload.message) {
      setState({ kind: "error", message: "Please fill out your name, email, and message." });
      return;
    }

    setState({ kind: "submitting" });

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || "Something went wrong. Please try again.");
      }

      form.reset();
      setState({ kind: "success" });
    } catch (err) {
      setState({
        kind: "error",
        message:
          err instanceof Error ? err.message : "Something went wrong. Please try again.",
      });
    }
  }

  if (state.kind === "success") {
    return (
      <div className={styles.success} role="status">
        <span className="eyebrow">Message sent</span>
        <h2>Thank you for reaching out.</h2>
        <p>
          I&rsquo;ll be in touch within two business days. If your matter is urgent or you are
          experiencing a mental health emergency, please call or text 988 or go to your nearest
          emergency room.
        </p>
      </div>
    );
  }

  const submitting = state.kind === "submitting";

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.row}>
        <label className={styles.field}>
          <span>Name</span>
          <input
            type="text"
            name="name"
            autoComplete="name"
            required
            disabled={submitting}
            placeholder="Your full name"
          />
        </label>

        <label className={styles.field}>
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            disabled={submitting}
            placeholder="you@example.com"
          />
        </label>
      </div>

      <div className={styles.row}>
        <label className={styles.field}>
          <span>Phone (optional)</span>
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            disabled={submitting}
            placeholder="(555) 555-5555"
          />
        </label>

        <label className={styles.field}>
          <span>What are you reaching out about?</span>
          <select name="inquiry" defaultValue="" disabled={submitting}>
            <option value="" disabled>
              Choose one
            </option>
            {inquiryOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className={styles.field}>
        <span>Message</span>
        <textarea
          name="message"
          rows={6}
          required
          disabled={submitting}
          placeholder="Share a little about what brings you here. You don't need to share more than feels comfortable."
        />
      </label>

      <label className={styles.checkboxField}>
        <input type="checkbox" name="consentTelehealth" disabled={submitting} />
        <span>
          I understand that Lea provides therapy via secure telehealth to clients located in
          California.
        </span>
      </label>

      {/* honeypot */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className={styles.honeypot}
        aria-hidden="true"
      />

      {state.kind === "error" ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button type="submit" variant="primary" size="lg" disabled={submitting}>
          {submitting ? "Sending…" : "Send message"}
        </Button>
        <p className={styles.fineprint}>
          Your information is sent securely and used only to respond to your inquiry.
        </p>
      </div>
    </form>
  );
}
